// tutor-contrib-branding plugin: begin buildtime imports
import { useContext } from 'react';
import { AppContext } from '@edx/frontend-platform/react';
// tutor-contrib-branding plugin: end buildtime imports

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}
// tutor-contrib-branding plugin: begin buildtime definitions
const SELECTED_THEME_VARIANT_STORAGE_KEY = 'selected-paragon-theme-variant';

function formatThemeVariantLabel(themeVariant) {
  return themeVariant
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getAutomaticThemeVariant(paragonThemeUrls) {
  const themeVariants = paragonThemeUrls?.variants || {};
  const themeVariantDefaults = paragonThemeUrls?.defaults || {};
  const themeVariantKeys = Object.keys(themeVariants);

  if (themeVariantKeys.length === 1) {
    return themeVariantKeys[0];
  }

  const prefersDarkMode = !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  if (prefersDarkMode && themeVariantDefaults.dark && themeVariants[themeVariantDefaults.dark]) {
    return themeVariantDefaults.dark;
  }

  if (themeVariantDefaults.light && themeVariants[themeVariantDefaults.light]) {
    return themeVariantDefaults.light;
  }

  return themeVariantKeys[0];
}

function getThemeVariantOptions(paragonThemeUrls) {
  const themeVariants = paragonThemeUrls?.variants || {};
  const themeVariantDefaults = paragonThemeUrls?.defaults || {};
  const themeVariantKeys = Object.keys(themeVariants);

  if (themeVariantKeys.length <= 1) {
    return [];
  }

  const options = [{ value: 'auto', label: 'Auto' }];
  const seenThemeVariants = new Set();

  if (themeVariantDefaults.light && themeVariants[themeVariantDefaults.light]) {
    options.push({ value: themeVariantDefaults.light, label: 'Light' });
    seenThemeVariants.add(themeVariantDefaults.light);
  }

  if (
    themeVariantDefaults.dark &&
    themeVariants[themeVariantDefaults.dark] &&
    themeVariantDefaults.dark !== themeVariantDefaults.light
  ) {
    options.push({ value: themeVariantDefaults.dark, label: 'Dark' });
    seenThemeVariants.add(themeVariantDefaults.dark);
  }

  themeVariantKeys
    .filter((themeVariant) => !seenThemeVariants.has(themeVariant))
    .sort()
    .forEach((themeVariant) => {
      options.push({
        value: themeVariant,
        label: formatThemeVariantLabel(themeVariant),
      });
    });

  return options;
}

function ThemeSelectorFooterWidget() {
  const { config, paragonTheme } = useContext(AppContext);
  const paragonThemeUrls = config?.PARAGON_THEME_URLS;
  const themeVariantOptions = getThemeVariantOptions(paragonThemeUrls);

  if (!paragonThemeUrls || themeVariantOptions.length === 0) {
    return null;
  }

  const storedThemeVariant = window.localStorage.getItem(SELECTED_THEME_VARIANT_STORAGE_KEY);
  const validOptionValues = new Set(themeVariantOptions.map(({ value }) => value));
  const selectedThemeVariant = (
    storedThemeVariant && validOptionValues.has(storedThemeVariant)
  ) ? storedThemeVariant : 'auto';

  const onThemeVariantChange = (event) => {
    const nextThemeVariant = event.target.value;

    if (nextThemeVariant === 'auto') {
      window.localStorage.removeItem(SELECTED_THEME_VARIANT_STORAGE_KEY);

      const automaticThemeVariant = getAutomaticThemeVariant(paragonThemeUrls);
      if (automaticThemeVariant && automaticThemeVariant !== paragonTheme?.state?.themeVariant) {
        window.location.reload();
      }
      return;
    }

    paragonTheme.setThemeVariant(nextThemeVariant);
  };

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
        marginTop: '1rem',
        width: '100%',
        whiteSpace: 'nowrap',
      }}
    >
      <label
        htmlFor="tutor-branding-theme-selector"
        style={{
          color: 'var(--pgn-color-text-base, inherit)',
          fontSize: '0.875rem',
          marginBottom: 0,
        }}
      >
        Theme
      </label>
      <select
        id="tutor-branding-theme-selector"
        aria-label="Theme selector"
        onChange={onThemeVariantChange}
        style={{
          backgroundColor: 'var(--pgn-color-light-100, #ffffff)',
          border: '1px solid var(--pgn-color-light-500, #c8c8c8)',
          borderRadius: '999px',
          color: 'var(--pgn-color-text-base, inherit)',
          fontSize: '0.875rem',
          padding: '0.25rem 0.75rem',
        }}
        value={selectedThemeVariant}
      >
        {themeVariantOptions.map(({ value, label }) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
}

function BrandingOverlayHtmlWidget() {
  const { config } = useContext(AppContext);
  const overlayHtml = config?.BRANDING_OVERLAY_HTML;

  if (!overlayHtml) {
    return null;
  }

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: overlayHtml,
      }}
    />
  );
}
// tutor-contrib-branding plugin: end buildtime definitions


async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
    addPlugins(config, 'footer_slot', [{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'branding_theme_selector',
    type: DIRECT_PLUGIN,
    priority: 80,
    RenderWidget: ThemeSelectorFooterWidget,
  },
}]);
    if (process.env.APP_ID == 'admin-console') {
    }
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {

      // Currently there is no way to conditionally add plugin slot configuration from tutor-mfe.
      // So, as a workaround, tutor-contrib-aspects simply defines the components added to the plugin as empty components.
      const CourseOutlineSidebar = () => {};
      const UnitPageSidebar = () => {};
      const SidebarToggleWrapper = ({component}) => component;
      const CourseHeaderButton = () => {};
      const UnitActionsButton = () => {};
      const SubSectionAnalyticsButton = () => {};

      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseOutlineSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'course-unit-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitPageSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'unit-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_unit_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitActionsButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_subsection_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: SubSectionAnalyticsButton,
                },
            }]);
    }
    if (process.env.APP_ID == 'account') {
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
    }
    if (process.env.APP_ID == 'learning') {
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
    }
    if (process.env.APP_ID == 'catalog') {
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;